/**
 * Captura dados de atribuicao (cookie _ga, UTMs, gclid, fbclid) para enviar ao backend
 * e permitir tracking server-side (Measurement Protocol via sGTM) com stitching de client_id.
 */

export interface AttributionData {
  /** Client ID do GA4 extraido do cookie _ga */
  gaClientId?: string;
  /** Session ID do GA4 (cookie _ga_<MEASUREMENT_ID>) */
  gaSessionId?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  gclid?: string;
  fbclid?: string;
  pageLocation?: string;
  pageReferrer?: string;
  userAgent?: string;
  language?: string;
}

const STORAGE_KEY = 'imobdesk_attribution';

/**
 * Le o cookie _ga e extrai o client_id no formato GA4 (timestamp.<id>).
 * Cookie _ga: GA1.1.1234567890.1234567890
 * Retornamos os 2 ultimos segmentos: "1234567890.1234567890"
 */
function readGaClientId(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(/(?:^|;\s*)_ga=([^;]+)/);
  if (!match) return undefined;
  const value = decodeURIComponent(match[1]);
  const parts = value.split('.');
  if (parts.length < 4) return undefined;
  return `${parts[parts.length - 2]}.${parts[parts.length - 1]}`;
}

/**
 * Le o cookie _ga_<MEASUREMENT_ID> e extrai o session_id.
 * Formato: GS1.1.<session_id>.1.0.<timestamp>.0.0.0
 */
function readGaSessionId(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(/(?:^|;\s*)_ga_[A-Z0-9]+=([^;]+)/);
  if (!match) return undefined;
  const value = decodeURIComponent(match[1]);
  const parts = value.split('.');
  if (parts.length < 3) return undefined;
  return parts[2];
}

/**
 * Coleta atribuicao da URL atual + cookies e persiste em sessionStorage
 * para sobreviver a navegacao entre paginas dentro do checkout.
 */
export function captureAttribution(): AttributionData {
  if (typeof window === 'undefined') return {};

  const search = new URLSearchParams(window.location.search);

  const fromUrl: AttributionData = {
    utmSource: search.get('utm_source') ?? undefined,
    utmMedium: search.get('utm_medium') ?? undefined,
    utmCampaign: search.get('utm_campaign') ?? undefined,
    utmContent: search.get('utm_content') ?? undefined,
    utmTerm: search.get('utm_term') ?? undefined,
    gclid: search.get('gclid') ?? undefined,
    fbclid: search.get('fbclid') ?? undefined,
  };

  const stored = readStored();

  const merged: AttributionData = {
    ...stored,
    ...Object.fromEntries(
      Object.entries(fromUrl).filter(([, value]) => value !== undefined && value !== ''),
    ),
    gaClientId: readGaClientId() ?? stored.gaClientId,
    gaSessionId: readGaSessionId() ?? stored.gaSessionId,
    pageLocation: window.location.href,
    pageReferrer: document.referrer || stored.pageReferrer,
    userAgent: navigator.userAgent,
    language: navigator.language,
  };

  persist(merged);
  return merged;
}

/**
 * Le a atribuicao mais recente (sessionStorage + cookies frescos).
 * Use no momento do submit do checkout para enviar ao backend.
 */
export function getAttribution(): AttributionData {
  return captureAttribution();
}

function readStored(): AttributionData {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as AttributionData;
  } catch {
    return {};
  }
}

function persist(data: AttributionData) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // ignore — sessionStorage indisponivel
  }
}
